import { useEffect, useState } from 'react';
import CameraOrbitControls from './CameraOrbitControls';
import RenderHistoryGrid from './RenderHistoryGrid';
import {
  RENDER_LIGHTING_OPTIONS,
  RENDER_RESOLUTION_OPTIONS,
} from '../utils/photorealistic/roomImageRenderInference';

const pillBaseClass =
  'w-full rounded-full border px-3 py-2 text-center text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/60';

function OptionPillGroup({ label, options, value, onChange, name }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-center text-[11px] font-semibold tracking-wide text-gray-500">
        {label}
      </span>
      <div className="flex flex-col gap-1.5" role="radiogroup" aria-label={label}>
        {options.map((option) => {
          const selected = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={selected}
              name={name}
              onClick={() => onChange(option.id)}
              className={`${pillBaseClass} ${
                selected
                  ? 'border-gray-800 bg-gray-800 text-white'
                  : 'border-gray-200 bg-white text-gray-800 hover:border-primary-300 hover:bg-primary-500/10'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const RenderPanel = ({
  renderResolution,
  onRenderResolutionChange,
  lightingPreset,
  onLightingPresetChange,
  onCameraChange,
  libraryItems = [],
  libraryLoading = false,
  libraryError = null,
  onLibraryItemSelect,
  onLibraryItemDelete,
  deletingLibraryItemId = null,
  onLibraryTabActive,
}) => {
  const [activeTab, setActiveTab] = useState('preferences');

  useEffect(() => {
    if (activeTab === 'library') {
      onLibraryTabActive?.();
    }
  }, [activeTab, onLibraryTabActive]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="mb-3 shrink-0">
        <h3 className="text-[16px] lao-mn text-gray-700">Photo-Realistic Renders</h3>
      </div>

      <div className="mb-3 flex shrink-0 gap-1.5">
        <button
          type="button"
          onClick={() => setActiveTab('preferences')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-full border px-2 py-1.5 text-[11px] font-medium transition-colors ${
            activeTab === 'preferences'
              ? 'border-gray-800 bg-gray-800 text-white'
              : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          Preferences
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('library')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-full border px-2 py-1.5 text-[11px] font-medium transition-colors ${
            activeTab === 'library'
              ? 'border-gray-800 bg-gray-800 text-white'
              : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          Render Library
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pr-0.5">
      {activeTab === 'preferences' ? (
        <div className="flex flex-col gap-4 px-0.5 py-1 pb-3">
          <p className="shrink-0 text-center text-sm font-semibold text-gray-700">Preferences</p>

          <CameraOrbitControls variant="render" onCameraChange={onCameraChange} />

          <OptionPillGroup
            label="Render resolution"
            name="render-resolution"
            options={RENDER_RESOLUTION_OPTIONS}
            value={renderResolution}
            onChange={onRenderResolutionChange}
          />

          <OptionPillGroup
            label="Lighting preset"
            name="lighting-preset"
            options={RENDER_LIGHTING_OPTIONS}
            value={lightingPreset}
            onChange={onLightingPresetChange}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3 px-0.5 py-1 pb-3">
          <p className="shrink-0 text-center text-sm font-semibold text-gray-700">Render Library</p>

          {libraryLoading ? (
            <p className="text-center text-xs text-gray-500 py-4">Loading renders…</p>
          ) : libraryError ? (
            <p className="text-center text-xs text-red-600 py-4">{libraryError}</p>
          ) : libraryItems.length === 0 ? (
            <p className="text-center text-xs text-gray-500 py-8">
              No saved renders yet. Generate a preview and save it to build your library.
            </p>
          ) : (
            <RenderHistoryGrid
              items={libraryItems}
              columns={2}
              onSelectItem={onLibraryItemSelect}
              onDeleteItem={onLibraryItemDelete}
              deletingItemId={deletingLibraryItemId}
              enableExport
            />
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default RenderPanel;
